import { IMoveTestCaseSchema, ModuleService, moveTestCaseSchema } from '@/api/module';
import { Button, Dialog, Modal } from 'alurkerja-ui';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { FaRegCopy } from 'react-icons/fa';
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { BsArrowReturnRight } from 'react-icons/bs';
import { TestCaseService } from '@/api/testCase';
import { SelectTreeField } from '@/components/form/fields/SelectTreeField';
import { Section } from '@/components/Section';
import { Loading } from '@/components/Loading';
import { useParams } from 'react-router-dom';

type MoveTestCaseProps = {
  onSuccess: () => void;
  testcases: string[];
  triggerButtonType?: 'list' | 'module';
};

interface ModalContentProps extends MoveTestCaseProps {
  closeModal?: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
  closeModal,
  onSuccess,
  testcases,
}) => {
  const { id } = useParams();

  const {
    mutate: mutateMoveTestCase,
    isPending: isLoadingMoveTestCase,
  } = ModuleService.useMoveTestCase();
  const { data: optionsData, isFetching: isOptionsFetching } =
    TestCaseService.useGetTypeAndPriority(id);

  const form = useForm<IMoveTestCaseSchema>({
    resolver: zodResolver(moveTestCaseSchema),
    defaultValues: {
      idTestCase: testcases,
    },
  });

  const submitAction = form.handleSubmit(data => {
    mutateMoveTestCase({
      payload: data,
    }, {
      onSuccess: () => {
        Dialog.success({
          title: 'Success',
          description: 'Test case has been moved',
          callback: () => {
            onSuccess();
            closeModal?.();
          },
        });
      },
      onError: () => {
        Dialog.error({
          title: 'Error',
          description: 'Failed to move test case',
        });
      },
    });

  });

  if (isOptionsFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <FormContainer form={form}>
      <SelectTreeField
        name="moduleId"
        label="Select User Story"
        options={optionsData?.module}
        hasRequiredAsterisk
      />

      <FormFooter>
        <OutlineButton onClick={closeModal}>Cancel</OutlineButton>
        <Button loading={isLoadingMoveTestCase} onClick={submitAction}>Ok</Button>
      </FormFooter>
    </FormContainer>
  );
};

export const MoveTestCase: React.FC<MoveTestCaseProps> = props => {
  const { triggerButtonType } = props;

  const triggerButton =
    triggerButtonType === 'list' ? (
      <Button
        variant="filled"
        icon={<BsArrowReturnRight size={18} className="text-white" />}
        type="button"
        className="bg-green-600"
        disabled={!props.testcases.length}
      >
        Move Test Case
      </Button>
    ) : (
      <LinkButton icon={<FaRegCopy className="text-green-600" />} />
    );

  return (
    <Modal
      title="Move Test Case"
      triggerButton={triggerButton}
      maxWidth="7xl"
    >
      {({ closeModal }) => (
        <div className="p-4">
          <ModalContent closeModal={closeModal} {...props} />
        </div>
      )}
    </Modal>
  );
};